import {
  compressToEncodedURIComponent,
  decompressFromEncodedURIComponent,
} from 'lz-string';

/**
 * Helpers for putting an Elasticsearch query object into the `search` page URL
 * without blowing the URL length up.
 *
 * The query is the full ES DSL, which is extremely repetitive: the bulk of it is
 * long, recurring field names and structural keywords (`simple_query_string`,
 * `availableForSale`, `must_not`, ...). We shrink the URL in two stages:
 *
 *   1. Dictionary substitution: every known ES token is replaced by a short,
 *      sentinel-wrapped code (see DICTIONARY). This shrinks the *first*
 *      occurrence of each token (which LZ cannot dedupe) as well as the rest.
 *   2. LZ compression to a short, URL-safe token.
 *
 * Reading is backward compatible: the dictionary-reverse pass is a no-op on
 * plain LZ tokens (old format), and raw-JSON legacy links still parse.
 *
 * IMPORTANT: DICTIONARY is append-only. Never reorder or remove entries — the
 * codes are positional, so changing them would break links already shared.
 */

/**
 * Sentinel from the Unicode Private Use Area. It never appears in normal query
 * data, so a `SENTINEL + code + SENTINEL` marker can never collide with real
 * JSON content. Encoding is skipped (see below) on the vanishingly rare chance
 * a query literally contains this character.
 */
const SENTINEL = '';

/**
 * Known ES tokens, most-valuable (longest / most frequent) first.
 * APPEND-ONLY — see the note above.
 */
const DICTIONARY: string[] = [
  // structural keywords
  'simple_query_string',
  'must_not',
  'should',
  'filter',
  'match',
  'terms',
  'range',
  'exists',
  'field',
  'query',
  'sort',
  'nested',
  'wildcard',
  'bool',
  'term',
  'must',
  'gte',
  'lte',
  'gt',
  'lt',
  // common fields
  'Rapnet_pluspercarat',
  'availableForSale',
  'standardShapeCode',
  'internalStatus',
  'externalStatus',
  'FMInscription',
  'StainedGlass',
  'department',
  'transType',
  'ShapeCode',
  'ColorCode',
  'location',
  // common values
  'KAM Sales',
  'On Memo',
  'aspecony',
  'ASHOKA',
  'FANCY',
  'ROUND',
  'Sales',
  'Sold',
];

/** Wrap a dictionary index into its sentinel-delimited code. */
function codeFor(index: number): string {
  return SENTINEL + index.toString(36) + SENTINEL;
}

/** Replace every known token (as a quoted JSON string) with its short code. */
function dictionaryEncode(json: string): string {
  let out = json;
  for (let i = 0; i < DICTIONARY.length; i++) {
    out = out.split(JSON.stringify(DICTIONARY[i])).join(codeFor(i));
  }
  return out;
}

/** Reverse dictionaryEncode. A no-op when no codes are present. */
function dictionaryDecode(sub: string): string {
  if (sub.indexOf(SENTINEL) === -1) {
    return sub;
  }
  let out = sub;
  for (let i = 0; i < DICTIONARY.length; i++) {
    out = out.split(codeFor(i)).join(JSON.stringify(DICTIONARY[i]));
  }
  return out;
}

/** Encode a query object into a short, URL-safe token for `?query=`. */
export function encodeSearchQuery(query: any): string {
  if (query === null || query === undefined) {
    return '';
  }
  const json = JSON.stringify(query);
  // Guard: if the raw data somehow contains the sentinel, skip substitution so
  // decoding stays lossless. LZ compression still applies.
  const payload = json.indexOf(SENTINEL) === -1 ? dictionaryEncode(json) : json;
  return compressToEncodedURIComponent(payload);
}

/**
 * Decode a `?query=` param back into the query object.
 * Accepts the compressed token (dictionary + LZ, or plain LZ) and raw JSON
 * (legacy links). Returns `null` if the value cannot be decoded.
 */
export function decodeSearchQuery(param: string): any {
  if (!param) {
    return null;
  }

  // New format: LZ-compressed token, optionally dictionary-substituted.
  try {
    const decompressed = decompressFromEncodedURIComponent(param);
    if (decompressed) {
      return JSON.parse(dictionaryDecode(decompressed));
    }
  } catch {
    // fall through to legacy parsing
  }

  // Legacy format: raw JSON in the URL.
  try {
    return JSON.parse(param);
  } catch {
    return null;
  }
}
